import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownButton, Container, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function Purchases() {
  const { token, isAuthenticated } = useContext(AuthContext); 
  const [cellContents, setCellContents] = useState({});  
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(localStorage.getItem('filterPurchaseStatus') || 'processing');
  const [filterTitle, setFilterTitle] = useState(localStorage.getItem('filterPurchaseStatusTitle') || 'Processing');
  const [shippingDetails, setShippingDetails] = useState({});
  const [crtDetails, setCrtDetails] = useState({}); // Store CRT details by slug
  const [fetchingCrts, setFetchingCrts] = useState(new Set()); // Track CRTs being fetched
  const { stores, selectedStore, setSelectedStore, setStoreLocalStorage, setStoreLogo, fetchStores } = useContext(StoreContext);

  // Filter purchases based on status
  const filteredPurchases = purchases.filter((purchase) => {
    if (purchase.status !== 'succeeded') {
      return false;
    }

    if (purchase.type === 'invoice') {
      return false;
    }

    switch (filter) {
      case 'processing':
        return !purchase.shipping_status || purchase.shipping_status === 'processing';
      case 'labelCreated':
        return purchase.shipping_status === 'label_created';
      case 'packaged':
        return purchase.shipping_status === 'packaged';
      case 'inTransit':
        return purchase.shipping_status === 'in_transit';
      case 'delivered':
        return purchase.shipping_status === 'delivered';
      case 'suspended':
        return purchase.shipping_status === 'suspended';
      case 'returned':
        return purchase.shipping_status === 'returned';
      case 'canceled':
        return purchase.shipping_status === 'canceled';
      case 'failed':
        return purchase.shipping_status === 'failed';
      default:
        return !purchase.shipping_status || purchase.shipping_status === 'processing';
    }
  });

  useEffect(() => {
    if (isAuthenticated && token) {
      //console.log('Token:', token); // Debugging statement
      fetchStores();
    } else {
      console.log('Token is not available'); // Debugging statement
    }
  }, [token, filter]); // Don't add fetchStores as a dependecy

  // Fetch products for the selected store
  useEffect(() => {
    if (selectedStore) {
      handleFetchPurchases(selectedStore);
    }
  }, [selectedStore]);

  // Fetch CRT details for purchases with CRT models
  useEffect(() => {
    if (filteredPurchases.length > 0 && token) {
      const slugsToFetch = new Set();
      
      filteredPurchases.forEach(purchase => {
        if (purchase.crt_models?.length > 0) {
          purchase.crt_models.forEach(crt => {
            if (crt.slug) {
              slugsToFetch.add(crt.slug);
            }
          });
        }
      });

      if (slugsToFetch.size > 0) {
        fetchCrtDetails(Array.from(slugsToFetch));
      }
    }
  }, [filteredPurchases]);

  // Function to update the content of a specific cell
  const handleCellContentUpdate = (cellId, newContent) => {
    setCellContents(prevContents => ({
      ...prevContents,
      [cellId]: newContent
    }));
  };

  const buildOrderSummary = (items, separator) => {
    if (!items || items.length === 0) return;
  
    const lines = [
      `\n---`,
      `\n**Order Summary**\n` // Add a title here
    ];
  
    lines.push(...items.map(({ description, quantity }) =>
      `**${quantity}** x ${description}`
    ));
  
    const total = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    lines.push(`\n**Total items:** ${total}`);
    lines.push(`\n---`);
  
    return lines.join(separator);
  };

  // Fetch purchases logic
  const handleFetchPurchases = async (storeId) => {
    setLoading(true);
    setError(null);
    console.log('Fetch Purchases for store id: ' + storeId);

    if (token) {
      try {

        // Fetch user settings for shipping dimensions
        const settingsResponse = await fetch(`${STORE_API_URL}/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!settingsResponse.ok) {
          const errorData = await settingsResponse.json();
          throw new Error(errorData.error || 'Error fetching user settings.');
          //throw new Error('Error fetching user settings.');
        }
        const userSettings = await settingsResponse.json();

        // Fetch purchases
        const response = await fetch(`${STORE_API_URL}/purchases/store/${storeId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error fetching purchases. Please check your authentication.');
        }
        const purchases = await response.json();
        setPurchases(purchases);

        // Initialize shipping details with default values for each purchase
        const initialShippingDetails = {};
        purchases.forEach(purchase => {
          initialShippingDetails[purchase._id] = {
            height: userSettings.shipping ? userSettings.shipping.height : '2',
            width: userSettings.shipping ? userSettings.shipping.width : '6',
            length: userSettings.shipping ? userSettings.shipping.length : '4',
            weight: userSettings.shipping ? userSettings.shipping.weight : '100'
          };
        });
        setShippingDetails(initialShippingDetails);

      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleShippingLabelOrderClick = async (purchase, vendor) => {
    const userConfirmed = window.confirm('This will place an order for the shipping label and your CC will be charged. Good?');
    if (userConfirmed) {

      const shippingDetailsForPurchase = shippingDetails[purchase._id] || {
        height: '2',
        width: '5',
        length: '5',
        weight: '100'
      };

      const customFieldsString = purchase.custom_fields && purchase.custom_fields
        .filter(field => field.text.value)
        .map(field => `${field.label.custom}: ${field.text.value}`)
        .join(', ');

      console.log('Order Shipping Label. Custom Fields: ' + customFieldsString);

      if (vendor === 'sendle') {
        try {
          const response = await fetch(`${STORE_API_URL}/shipping/sendle/order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              purchase: purchase,
              description: customFieldsString,
              ...shippingDetailsForPurchase
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.log('Sendle error response:', errorData);
            throw new Error(errorData.error?.error_description || 
                           (typeof errorData.error === 'string' ? errorData.error : 
                           JSON.stringify(errorData.error) || 'Error ordering the label.'));
          } else {
            toast.success("Shipment created on Sendle!");
            if (selectedStore) {
              handleFetchPurchases(selectedStore);
            }
            setLoading(false);
          }

        } catch (error) {
          toast.error(`Sendle: ${error.message}`);
          //setError(error.message);
        }
      } else if (vendor === 'chitchats') {
        try {
          const response = await fetch(`${STORE_API_URL}/shipping/chitchats/order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              purchase: purchase,
              description: customFieldsString,
              ...shippingDetailsForPurchase
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(JSON.stringify(errorData.messages) || 'Error creating shipment');
          } else {
            toast.success("Shipment created on ChitChats!");
            if (selectedStore) {
              handleFetchPurchases(selectedStore);
            }
            setLoading(false);
          }

        } catch (error) {
          toast.error(`ChitChats: ${error.message}`);
        }
      }
    }
  };

  const handleShippingInputChange = (e, purchaseId) => {
    const { name, value } = e.target;
    setShippingDetails(prevState => ({
      ...prevState,
      [purchaseId]: {
        ...prevState[purchaseId],
        [name]: value
      }
    }));
  };

  const handleShippingQuotesSubmit = async (e, purchase) => {
    toast.success(`Fetching shipping quotes`);
    e.preventDefault();

    const shippingDetailsForPurchase = shippingDetails[purchase._id] || {
      height: '2',
      width: '5',
      length: '5',
      weight: '100'
    };

    const customFieldsString = purchase.custom_fields && purchase.custom_fields
      .filter(field => field.text.value)
      .map(field => `${field.label.custom}: ${field.text.value}`)
      .join(', ');

    console.log('Custom Fields: ' + customFieldsString);

    const payload = {
      purchase: purchase,
      description: customFieldsString,
      ...shippingDetailsForPurchase
    };

    let sendleQuote = null;
    let chitchatsQuotes = [];

    try {
      const sendleResponse = await fetch(`${STORE_API_URL}/shipping/sendle/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!sendleResponse.ok) {
        const errorData = await sendleResponse.json();
        throw new Error(JSON.stringify(errorData.message) || 'Error submitting the Sendle shipping form');
      } else {
        const sendleData = await sendleResponse.json();
        const sendleResult = sendleData.find(item => item.product.code === "STANDARD-PICKUP");
        const sendleGrossAmount = sendleResult.quote.gross.amount;
        const sendleCurrency = sendleResult.quote.net.currency;

        sendleQuote = {
          amount: sendleGrossAmount,
          currency: sendleCurrency,
          service: 'Sendle'
        };
      }
    } catch (error) {
      toast.error(`Sendle: ${error.message}`);
    }

    try {
      const chitchatsResponse = await fetch(`${STORE_API_URL}/shipping/chitchats/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      if (!chitchatsResponse.ok) {
        const errorData = await chitchatsResponse.json();
        throw new Error(JSON.stringify(errorData.messages) || 'Error submitting the ChitChats shipping form');
      } else {
        const chitchatsData = await chitchatsResponse.json();
        const excludedPostageTypes = ['chit_chats_collect', 'usps_priority'];

        chitchatsQuotes = chitchatsData.shipment.rates
          .filter(rate => !excludedPostageTypes.includes(rate.postage_type))
          .map(rate => ({
            amount: rate.payment_amount,
            currency: 'CAD',
            service: rate.postage_description
          }));

      }
    } catch (error) {
      toast.error(`ChitChats: ${error.message}`);
    }

    handleCellContentUpdate(purchase._id,
      <div className='mb-2'>
        {sendleQuote && (
          <button className='btn btn-primary btn-sm' onClick={() => handleShippingLabelOrderClick(purchase, 'sendle')}>
            ${sendleQuote.amount} {sendleQuote.currency} <br />
            {sendleQuote.service}
          </button>
        )}
        {chitchatsQuotes.length > 0 && chitchatsQuotes.map((quote, index) => (
          <button className='btn btn-primary btn-sm' style={{marginLeft:'.5rem'}} key={index} onClick={() => handleShippingLabelOrderClick(purchase, 'chitchats')}>
            ${quote.amount} {quote.currency} <br />
            {quote.service}
          </button>
        ))}
      </div>
    );

    setLoading(false);
  };

  const handleShippingCheckStatus = async (purchase) => {
    try {
      const { shipping_info } = purchase;
  
      console.log('Checking email status: ' + JSON.stringify(shipping_info));
      setLoading(true);
  
      if (shipping_info.sendle) {
        await handleSendleShippingStatus(purchase);
      } else if (shipping_info.chitchats) {
        await handleChitChatsShippingStatus(purchase);
      } else {
        console.log('No known shipping vendor found in shipping_info.');
        toast.error('Unknown shipping vendor. Unable to check status.');
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error('Error updating shipping status: ' + (error.message || error));
      console.error('Error updating shipping status:', error);
    }
  };

  const handleSendleShippingStatus = async (purchase) => {
    try {
      const response = await fetch(`${STORE_API_URL}/shipping/sendle/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ purchase })
      });
  
      const sendleResponse = await response.json();
  
      if (!response.ok) {
        console.log('ErrorData: ' + JSON.stringify(sendleResponse));
        throw new Error(sendleResponse.error || 'Error fetching Sendle shipping status. Please check your request.');
      }
  
      toast.success(`Shipping status updated to: ${sendleResponse.state}`);
      if (sendleResponse.email_status && sendleResponse.email_status.shipment_email) {
        toast.success('Email sent or already sent!');
        handleFetchPurchasesIfStoreExists();
      } else {
        console.error('Email not sent', JSON.stringify(sendleResponse));
        toast.error('Email not sent.');
      }
    } catch (error) {
      setLoading(false);
      toast.error((error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleChitChatsShippingStatus = async (purchase) => {
    try {
      const response = await fetch(`${STORE_API_URL}/shipping/chitchats/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ purchase })
      });
  
      const chitchatsResponse = await response.json();
  
      if (!response.ok) {
        console.log('ErrorData: ' + JSON.stringify(chitchatsResponse));
        throw new Error(chitchatsResponse.error || 'Error fetching ChitChats shipping status. Please check your request.');
      }
  
      toast.success(`Shipping status updated to: ${chitchatsResponse.shipment.status}`);
      if (chitchatsResponse.email_status && chitchatsResponse.email_status.shipment_email) {
        toast.success('Email sent or already sent!');
        handleFetchPurchasesIfStoreExists();
      } else {
        console.error('Email not sent', JSON.stringify(chitchatsResponse.error));
        toast.error('Email not sent.');
      }
    } catch (error) {
      setLoading(false);
      toast.error((error.message || error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleFetchPurchasesIfStoreExists = () => {
    if (selectedStore) {
      handleFetchPurchases(selectedStore);
    }
  };

  const handleShippingStatus = async (purchase, status) => {
    try {
      const response = await fetch(`${STORE_API_URL}/purchases/update-shipping-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purchase: purchase,
          status: status
        }),
      });

      if (response.ok) {
        toast.success("Shipping status updated to " + status);
        if (selectedStore) {
          handleFetchPurchases(selectedStore);
        }
      } else {
        const errorData = await response.json();
        toast.error("Error updating shipping status: " + JSON.stringify(errorData));
        console.error('Error updating shipping status:', errorData);
      }
    } catch (error) {
      toast.error("Error updating shipping status: " + error);
      console.error('Error updating shipping status:', error);
    }
  };

  const handleSelect = (filter, title) => {
    setFilter(filter);
    setFilterTitle(title);

    // Store the filter and title in localStorage
    localStorage.setItem('filterPurchaseStatus', filter);
    localStorage.setItem('filterPurchaseStatusTitle', title);
  };

  const handleStoreChange = (e) => {
    const newStoreId = e.target.value;
    setSelectedStore(newStoreId);
    setStoreLocalStorage(newStoreId)

    const selected = stores.find(store => store._id === newStoreId);
    setStoreLogo(selected ? selected.logo : '');
  };

  const getShippingPartner = (purchase) => {
    if (purchase.shipping_info) {
      if (purchase.shipping_info.chitchats) {
        return 'chitchats';
      }
      if (purchase.shipping_info.sendle) {
        return 'sendle';
      }
    }
    return 'LABEL NOT CREATED';
  };

  const TrackingLink = ({ purchase }) => {
    const getTrackingUrl = () => {
      if (purchase.shipping_info) {
        if (purchase.shipping_info.chitchats && purchase.shipping_info.chitchats.shipment) {
          return purchase.shipping_info.chitchats.shipment.tracking_url;
        }
        if (purchase.shipping_info.sendle) {
          return purchase.shipping_info.sendle.tracking_url;
        }
      }
      return null;
    };

    const url = getTrackingUrl();

    return (
      <a href={url || '#'} target='_blank' rel="noopener noreferrer">
        {purchase.shipping_id}
      </a>
    );
  };

  // Helper functions for formatting MUX board information
  const formatResistance = (value) => {
    if (!value) return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    if (numValue === 0) return 'short/jump';
    
    if (numValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(1)}MΩ`;
    } else if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}kΩ`;
    } else {
      return `${numValue}Ω`;
    }
  };

  const formatBooleanValue = (value) => {
    if (value === 'true' || value === true) return 'Yes';
    if (value === 'false' || value === false) return 'No';
    return value || 'Unknown';
  };

  // Function to fetch CRT details for MUX board information
  const fetchCrtDetails = async (slugs) => {
    // Filter out slugs that are already fetched or being fetched
    const slugsToFetch = slugs.filter(slug => !crtDetails[slug] && !fetchingCrts.has(slug));
    
    if (slugsToFetch.length === 0) return;

    // Add slugs to fetching set
    setFetchingCrts(prev => {
      const newSet = new Set(prev);
      slugsToFetch.forEach(slug => newSet.add(slug));
      return newSet;
    });

    try {
      const promises = slugsToFetch.map(slug =>
        fetch(`${STORE_API_URL}/crts/circuit/${slug}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).then(async response => {
          if (response.ok) {
            const data = await response.json();
            return { slug, data };
          }
          console.error(`Failed to fetch CRT details for ${slug}`);
          return { slug, data: null };
        })
      );

      const results = await Promise.all(promises);
      
      // Update state with all fetched results
      setCrtDetails(prev => {
        const newDetails = { ...prev };
        results.forEach(({ slug, data }) => {
          if (data) {
            newDetails[slug] = data;
          }
        });
        return newDetails;
      });
    } catch (error) {
      console.error('Error fetching CRT details:', error);
    } finally {
      // Remove slugs from fetching set
      setFetchingCrts(prev => {
        const newSet = new Set(prev);
        slugsToFetch.forEach(slug => newSet.delete(slug));
        return newSet;
      });
    }
  };

  // Function to render MUX board information
  const renderMuxBoardInfo = (crt) => {
    if (!crt) return null;

    const hasMuxInfo = crt.mux_termination_resistors || 
                      crt.mux_rgb_input_resistors || 
                      crt.mux_rgb_input_capacitors ||
                      crt.mux_audio_resistors || 
                      crt.mux_blanking_diode ||
                      crt.mux_transistor ||
                      crt.mux_blanking_ground_resistor ||
                      crt.mux_blanking_inline_resistor ||
                      crt.mux_transistor_base_resistor;

    if (!hasMuxInfo) return null;

    // Calculate packing list by grouping components
    const packingList = {};
    
    // Helper function to add components to packing list
    const addToPacking = (value, count, unit = 'Ω') => {
      if (!value) return;
      const formattedValue = unit === 'μF' ? `${value}${unit}` : formatResistance(value);
      const key = `${formattedValue} ${unit === 'μF' ? 'capacitors' : 'resistors'}`;
      packingList[key] = (packingList[key] || 0) + count;
    };

    // Add resistors to packing list
    if (crt.mux_termination_resistors) {
      addToPacking(crt.mux_termination_resistors, 3); // R1, R2, R3
    }
    if (crt.rgb_direct_injection === 'true' || crt.rgb_direct_injection === true) {
      addToPacking(crt.mux_rgb_input_capacitors, 3, 'μF');
    } else {
      addToPacking(crt.mux_rgb_input_resistors, 3); // R4, R5, R6
    }
    if (crt.mux_audio_resistors) {
      addToPacking(crt.mux_audio_resistors, 2); // R7, R8
    }
    if (crt.mux_blanking_ground_resistor) {
      addToPacking(crt.mux_blanking_ground_resistor, 1); // R10
    }
    if (crt.mux_blanking_inline_resistor) {
      addToPacking(crt.mux_blanking_inline_resistor, 1); // R11
    }
    if (crt.mux_transistor_base_resistor) {
      addToPacking(crt.mux_transistor_base_resistor, 1); // R12
    }

    // Add diodes
    let totalDiodes = 0;
    if (crt.chassis_r_g_b_diodes_added === 'true' || crt.chassis_r_g_b_diodes_added === true) {
      totalDiodes += 3; // Chassis RGB diodes (only if they need to be added)
    }
    if (crt.chassis_blanking_diode_added === 'true' || crt.chassis_blanking_diode_added === true) {
      totalDiodes += 1; // Chassis blanking diode (only if it needs to be added)
    }
    if (crt.mux_blanking_diode === 'true' || crt.mux_blanking_diode === true) {
      totalDiodes += 1; // D9 MUX blanking diode (always needed if enabled)
    }
    if (totalDiodes > 0) {
      packingList[`1N4148 diodes`] = totalDiodes;
    }

    // Add transistor
    if (crt.mux_transistor === 'true' || crt.mux_transistor === true) {
      packingList[`PN2222A transistors`] = 1; // Q1
    }

    // Add screws
    packingList[`Stainess steel screws`] = 2; 

    // Add ribbon cable if specific MUX board kits are available
    const muxKitsRequiringRibbon = ['1.0B RGB LUMA MUX KIT', '1.3B RGB MUX KIT', '1.3C RGB MUX KIT', 'RGB MICRO KIT'];
    if (crt.mod_boards && crt.mod_boards.some(board => {
      // Handle both string and object formats
      const boardName = typeof board === 'string' ? board : board.name;
      return muxKitsRequiringRibbon.includes(boardName);
    })) {
      packingList[`Ribbon cable`] = 1;
    }
    return (
      <div className="p-2 mb-2" style={{ backgroundColor: 'rgb(32 0 33)', borderRadius: '5px', borderWidth: '1px', borderStyle: 'solid', borderColor: '#ea3377', fontSize: '0.85em' }}>
        <div className="mod-labels">
          {crt.chassis && (
            <span className="mod-label mod-chassis-label">
              {crt.chassis}
            </span>
          )}
          {/* Available MUX Board Kits */}
          {crt.mod_boards && crt.mod_boards.length > 0 && 
            crt.mod_boards.map((board, index) => (
              <span key={index} className="mod-label mod-board-label">
                {typeof board === 'string' ? board : board.name}
              </span>
            )
          )}
        </div>
        
        <div className="row">
          {/* Left Column: MUX Board Components */}
          <div className="col-md-6">
            <strong style={{ color: 'white' }}>MUX Board Components:</strong>
            <table className="table table-sm mt-1" style={{ fontSize: '0.8em' }}>
              <tbody>
                {crt.mux_termination_resistors && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R1, R2, R3 (Term.)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_termination_resistors)}</td>
                  </tr>
                )}
                <tr>
                  <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R4, R5, R6 (RGB)</td>
                  {crt.rgb_direct_injection === 'true' || crt.rgb_direct_injection === true ? (
                    <td className="text-end" style={{ color: 'white' }}>{crt.mux_rgb_input_capacitors}μF</td>
                  ):(
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_rgb_input_resistors)}</td>
                  )}
                </tr>
                {crt.mux_audio_resistors && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R7, R8 (Audio)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_audio_resistors)}</td>
                  </tr>
                )}
                {crt.mux_blanking_diode && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>D9 (Blanking)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatBooleanValue(crt.mux_blanking_diode)}</td>
                  </tr>
                )}
                {crt.mux_blanking_ground_resistor && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R10 (Blank Gnd)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_blanking_ground_resistor)}</td>
                  </tr>
                )}
                {crt.mux_blanking_inline_resistor && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R11 (Blank Inline)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_blanking_inline_resistor)}</td>
                  </tr>
                )}
                {crt.mux_transistor && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>Q1 (Transistor)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatBooleanValue(crt.mux_transistor)}</td>
                  </tr>
                )}
                {crt.mux_transistor_base_resistor && (
                  <tr>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>R12 (Trans Base)</td>
                    <td className="text-end" style={{ color: 'white' }}>{formatResistance(crt.mux_transistor_base_resistor)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Right Column: Packing List */}
          <div className="col-md-6">
            <strong style={{ color: 'white' }}>Packing List:</strong>
            <table className="table table-sm mt-1" style={{ fontSize: '0.8em' }}>
              <tbody>
                {Object.entries(packingList).map(([component, count]) => (
                  <tr key={component}>
                    <td className="fw-bold text-nowrap" style={{ color: 'white' }}>{count} x</td>
                    <td className="text-end" style={{ color: 'white' }}>{component}</td>
                  </tr>
                ))}
                {Object.keys(packingList).length === 0 && (
                  <tr>
                    <td colSpan="2" className="text-center text-muted" style={{ color: 'white' }}>No components required</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Purchases</h2>
        {selectedStore && (
          <>
            <div className="form-group">
              <label></label>
              <select
                id="storeSelect"
                className="form-select"
                value={selectedStore}
                onChange={handleStoreChange} // Use the handleStoreChange function
              >
                {stores.map((store) => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      {!loading && !error && selectedStore && (
        <>
          <div>
            <Link to={`/add-purchase/${selectedStore}`}>
              <h6>Add Purchase</h6>
            </Link>
          </div>
          <div className="filter-buttons">
            <DropdownButton id="dropdown-basic-button" title={`${filterTitle}`} align="end">
              <Dropdown.Item onClick={() => handleSelect('processing', 'Processing')}>Processing</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('labelCreated', 'Label Created')}>Label Created</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('packaged', 'Packaged')}>Packaged</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('inTransit', 'In Transit')}>In Transit</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('delivered', 'Delivered')}>Delivered</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('suspended', 'Suspended (Review)')}>Suspended (Review)</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect('canceled', 'Canceled')}>Canceled</Dropdown.Item>
            </DropdownButton>
          </div>
        </>
      )}

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {/*!loading && !error && !selectedStore && (
        <div className="col-md-12 mt-3">
          <div className="card mb-3 p-3" style={{ border: '2px solid #ff5722' }}>
            <div className="card-body">
              <h5 className="card-title" style={{ color: '#ff5722', fontWeight: 'bold' }}>Wait a minute...</h5>
              <p>To use this section, please set up your store in <Link to="/settings">Settings</Link>:</p>
              <ul className="ps-3">
                <li>To use the shipping feature you will need to setup your shipping provider keys in settings.</li>
                <li>Only two shipping providers are supported at this time for label creation. ChitChats (Canada), Sendle (Canada/US)</li>
                <li>You can also use your preferred shipping provider. However, you will have to manually handle the shipping label creation.</li>
                <li>Set up and connect a Stripe account to start accepting online payments.</li>
              </ul>
            </div>
          </div>
        </div>
      )*/}

      {!loading && !error && filteredPurchases.length > 0 && (
      <>
        <h4>Total records: {filteredPurchases.length}</h4>
        <div className="row">
          {filteredPurchases.map((purchase) => (
            <div className="col-md-12" key={purchase._id}>
              <div className="card mb-12" style={{ marginBottom: '10px' }}>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6" style={{ textAlign: 'left' }}>
                      <h6 className="card-title">
                        <strong>{purchase.name}</strong> <Link to={`/edit-purchase/${purchase._id}`}>Edit</Link>
                        <br />
                        {purchase.email}
                      </h6>
                      <small className="text-muted">
                        {purchase.created_at ? `Created: ${(purchase.created_at instanceof Date ? purchase.created_at : new Date(purchase.created_at)).toLocaleString()}` : null}
                        {purchase.updated_at ? ` | Updated: ${(purchase.updated_at instanceof Date ? purchase.updated_at : new Date(purchase.updated_at)).toLocaleString()}` : null}
                      </small>
                      <br />
                      {purchase.custom_fields && (
                        <div>
                          {purchase.custom_fields.map((field, index) => (
                            <span key={index}>
                              <strong>{field.label.custom}:</strong> {field.text && field.text.value ? field.text.value.toUpperCase() : ''}
                              <br />
                            </span>
                          ))}
                        </div>
                      )}
                      <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                        {buildOrderSummary(purchase.items, '\n') + (purchase.shipping?.notes ? `\n\n**Shipping Notes:**\n${purchase.shipping.notes}` : '')}
                      </ReactMarkdown>

                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <div className="card-text">
                        {purchase.shipping_status === 'label_created' ? (
                          purchase.shipping && purchase.shipping.address ? (
                            <div>
                              {
                              <button className='btn btn-danger me-2' onClick={() => handleShippingStatus(purchase, 'packaged')}>
                                Move to packaged
                              </button>
                              }
                              <button className='btn btn-primary' onClick={() => handleShippingCheckStatus(purchase)}>
                                Check Status
                              </button>
                            </div>
                          ) : null
                        ) : (purchase.shipping_status === 'packaged') ? (
                            purchase.shipping && purchase.shipping.address ? (
                              <div>
                                <button className='btn btn-primary' onClick={() => handleShippingCheckStatus(purchase)}>
                                  Check Status
                                </button>
                              </div>
                            ) : null
                        ) : (purchase.shipping_status === 'in_transit') ? (
                          purchase.shipping && purchase.shipping.address ? (
                            <div>
                              {/*
                              <button className='btn btn-danger me-2' onClick={() => handleShippingStatus(purchase, 'label_created')}>
                                Move back to Label Created
                              </button>
                              */}
                              <button className='btn btn-primary' onClick={() => handleShippingCheckStatus(purchase)}>
                                Check Status
                              </button>
                            </div>
                          ) : null
                        ) : (purchase.shipping && (!purchase.shipping_status || purchase.shipping_status === 'processing')) ? (
                          <div>
                            <div>
                              {cellContents[purchase._id] || ''}
                            </div>
                            <div className="d-flex justify-content-end">
                              <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center' }}>
                                W x H x L (in):
                                <div className="input-group" style={{  marginLeft: '10px', maxWidth: '300px' }}>
                                  <input
                                    type="text"
                                    name="height"
                                    className="form-control"
                                    placeholder="height (in)"
                                    style={{ textAlign: 'right' }}
                                    value={shippingDetails[purchase._id]?.height || '2'}
                                    onChange={(e) => handleShippingInputChange(e, purchase._id)}
                                  />
                                  <input
                                    type="text"
                                    name="width"
                                    placeholder="width (in)"
                                    className="form-control"
                                    style={{ textAlign: 'right' }}
                                    value={shippingDetails[purchase._id]?.width || '5'}
                                    onChange={(e) => handleShippingInputChange(e, purchase._id)}
                                  />
                                  <input
                                    type="text"
                                    name="length"
                                    placeholder="length (in)"
                                    className="form-control"
                                    style={{ textAlign: 'right' }}
                                    value={shippingDetails[purchase._id]?.length || '5'}
                                    onChange={(e) => handleShippingInputChange(e, purchase._id)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end">
                              <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center' }}>
                                Weight (g):
                                <input
                                  type="text"
                                  name="weight"
                                  className="form-control"
                                  style={{ width: '100px', marginLeft: '10px', textAlign: 'right' }}
                                  value={shippingDetails[purchase._id]?.weight || '100'}
                                  onChange={(e) => handleShippingInputChange(e, purchase._id)}
                                />
                              </div>
                            </div>
                            <button onClick={(e) => handleShippingQuotesSubmit(e, purchase)} className='btn btn-primary'>
                              Get Shipping Quotes
                            </button>
                          </div>
                        ) : (purchase.shipping_status === 'in_transit') ? (
                          <p>
                            Shipment Email: {(purchase.email_status && purchase.email_status.shipment_email ? 'Sent' : 'Not Sent')}
                          </p>
                        ) : null}
                        <div className='mt-3'>
                          <p>
                            ${(purchase.amount / 100).toFixed(2)} {purchase.currency && purchase.currency.toUpperCase()}
                          </p>
                          <p>
                            {purchase.shipping && purchase.shipping.address && (
                              <>
                                {purchase.shipping.address.line1}<br />
                                {purchase.shipping.address.line2 && <>{purchase.shipping.address.line2}<br /></>}
                                {purchase.shipping.address.city}<br />
                                {(purchase.shipping?.address?.state || purchase.shipping?.address?.country || purchase.shipping?.address?.postal_code) && (
                                  <>
                                    {purchase.shipping?.address?.state && `${purchase.shipping.address.state}`}
                                    {purchase.shipping?.address?.state && purchase.shipping?.address?.country && ', '}
                                    {purchase.shipping?.address?.country && `${purchase.shipping.address.country}`}
                                    {purchase.shipping?.address?.postal_code && ` (${purchase.shipping.address.postal_code})`}
                                    <br />
                                  </>
                                )}
                              </>
                            )}
                          </p>
                          <p>
                            <strong>{getShippingPartner(purchase).toUpperCase()}</strong>
                            <br />
                            <strong>{purchase.shipping_status && purchase.shipping_status.toUpperCase()}</strong>
                            <br />
                            <TrackingLink purchase={purchase} />
                          </p>
                          {purchase.crt_models?.length > 0 && (
                            <div>
                                {purchase.crt_models.map((crt, idx) => (
                                  <div key={idx}>
                                    {crt.slug ? (
                                      <div>
                                        <div className="d-flex align-items-center mb-2">
                                          <a
                                            href={`/crts/${crt.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="me-2"
                                          >
                                            {window.location.origin}/crts/{crt.slug}
                                          </a>
                                          <Button 
                                            as="a" 
                                            href={crt._id ? `/superadmin/edit-crt/${crt._id}` : `/crts/${crt.slug}`}
                                            variant="outline-primary" 
                                            size="sm"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {crt._id ? 'Edit CRT' : 'View CRT'}
                                          </Button>
                                        </div>
                                        {crtDetails[crt.slug] && renderMuxBoardInfo(crtDetails[crt.slug])}
                                      </div>
                                    ) : (
                                      <div>{crt.model}</div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
      )}
    </Container>
  );
}

export default Purchases;
